import React from 'react'
import clsx from 'clsx'
import { Headphone } from 'iconsax-react'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { useTheme } from '@mui/material'

import Spacer from '../../../../atoms/Spacer'
import CollapseButton from '../../../../atoms/CollapseButton'
import UserChannels from '../../../UserChannels'
import OwnerProvider, { useOwner } from '@SonarCX/providers/OwnerProvider'
import CopyableStateProvider from '@SonarCX/providers/CopyableStateProvider'

import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { removeChannel } from '@SonarCX/slices/user'
import useStyles from './styles'

const WrappedChannelSection = ({ user, canEdit }) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const { user: envUser, setEnvUser } = useEnvironment()
  const { setOwner, owner } = useOwner()
  const { down429, down770 } = useBreakpoint()
  const [expanded, setExpanded] = React.useState(true)
  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()

  const isEditing = Boolean(user)
  const classes = useStyles({
    isEditing,
    canEdit,
    smallMedia: down770,
    extraSmallMedia: down429
  })

  const handleDeleteChannelClick = React.useCallback(
    channel => {
      setTitle(`Delete ${channel.type === 'email' ? 'email' : 'phone number'}?`)
      setSubtext(' ')
      setReason(`Are you sure you want to delete ${channel?.value}?`)
      setV2(true)
      setAction(async () => {
        try {
          await dispatch(removeChannel(user.id, channel.id))
          dispatch(AddSuccessAlert('Communication deleted'))
          //update current user channels
          if (user.id !== envUser.id) return
          const newChannels = envUser.channels.filter(
            ch => ch.id !== channel.id
          )
          setEnvUser({ ...envUser, channels: [...newChannels] })
        } catch (err) {
          dispatch(AddErrorAlert({ err }))
        }
      })
    },
    [
      user,
      envUser,
      setTitle,
      setSubtext,
      setReason,
      setV2,
      setAction,
      dispatch,
      setEnvUser
    ]
  )

  const handleChannelAdd = React.useCallback(() => {
    dispatch(AddSuccessAlert('Communication saved'))
  }, [dispatch])

  const handleToggle = React.useCallback(() => {
    setExpanded(!expanded)
  }, [expanded])

  React.useEffect(() => {
    if (owner?.id !== user?.id) {
      setOwner(user)
    }
  }, [user, owner, setOwner])

  return (
    <>
      <div className={clsx(classes.root)}>
        {down770 && (
          <div className={classes.header}>
            <Headphone
              className='icon'
              variant='Bold'
              color='grey'
              size={theme.size.base}
            />
            Communications
            {isEditing && (
              <>
                <Spacer />

                <div className='buttons'>
                  {down770 && (
                    <CollapseButton open={expanded} onToggle={handleToggle} />
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {expanded && (
          <CopyableStateProvider>
            <UserChannels
              showOptions
              canAdd={canEdit}
              clickOnEdit={canEdit}
              user={user}
              onChannelAdd={handleChannelAdd}
              showVerifiedIcon
              onDelete={handleDeleteChannelClick}
            />
          </CopyableStateProvider>
        )}
      </div>
    </>
  )
}

const ChannelSection = ({ user, canEdit }) => (
  <OwnerProvider>
    <WrappedChannelSection user={user} canEdit={canEdit} />
  </OwnerProvider>
)

export default ChannelSection
